/**
 * Campanhas — template de marketing para uma lista com opt-in (spec 17 §9).
 *
 * ## A tela diz o custo ANTES do clique, e ele vem do servidor
 *
 * Quatro passos numa tela só: template → segmento (com a prévia) → horário e ritmo → **o custo em reais
 * e em conversas da franquia, e o botão**. Nada aqui calcula tarifa: a prévia é a resposta do
 * `POST /campanhas/previa`, da mesma função que grava. Duas contas divergiriam, e a da tela é a que
 * fica velha quando a Meta reajusta. O "Agendar" do cadastro fica desabilitado, com o motivo, até a
 * prévia chegar — e então leva no rótulo para quantos e quanto.
 *
 * ## As famílias (spec 22)
 *
 * - A lista é Consulta: `PaginaInteira` + `ListView` (Nome · Tipo · Estado · Criada · Fim).
 * - Nova campanha é Cadastro: `PaginaDeCadastro`, no lugar da lista (mesma rota).
 * - O detalhe de uma campanha é Detalhe, página própria por decisão do owner em 05/09 (*"pode até
 *   adotar a mesma coisa para os detalhes das campanhas"*): `/campanhas/<id>` (`CampanhaDetalhe`), com
 *   `Destaque`, a faixa de números, os destinatários no principal e o resto na lateral.
 *   `?campanha=<id>` continua chegando lá, por redirecionamento.
 */

import { useEffect, useId, useState, type KeyboardEvent, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { EstadoDaFalha, Metricas, contar, falhaDe, identificadorLegivel, textos, useSessao, type FalhaDaApi, type Metrica } from "@hugi/ui";
import {
  BarraDeAcoes, BlocoDoCadastro, CabecalhoDaTela, CampoDoCadastro, Carregando, Destaque, Dica, Erro, ListView, PaginaDeCadastro,
  PaginaDeRegistro, PaginaInteira, Selo, SemPermissao, Vazio,
  type AcaoDoRegistro, type ColunaDaLista, type VistaDaLista,
} from "@hugi/ui/kit";
import { CheckCheck, Megaphone, MessageCircleReply, Send, SquareKanban, UserMinus, Users, Wallet } from "lucide-react";
import {
  cancelarCampanha, criarCampanha, pausarCampanha, previaDeCampanha, retomarCampanha, sincronizarLimiteDoNumero,
  useCampanhas, useCatalogos, useConexoes, useDestinatarios, useEstatisticasDaCampanha, useTemplates,
  type Campanha, type EstatisticasDaCampanha, type PreviaDaCampanha, type ResultadoDoDestinatario,
} from "./dados.js";
import "./hugi-grupos.css";

type TomDoKit = "neutro" | "sucesso" | "atencao" | "perigo";

const ESTADO: Readonly<Record<Campanha["estado"], { rotulo: string; tom: TomDoKit }>> = {
  rascunho: { rotulo: "Rascunho", tom: "neutro" },
  agendada: { rotulo: "Agendada", tom: "neutro" },
  enviando: { rotulo: "Enviando", tom: "sucesso" },
  pausada: { rotulo: "Pausada", tom: "atencao" },
  concluida: { rotulo: "Concluída", tom: "sucesso" },
  cancelada: { rotulo: "Cancelada", tom: "perigo" },
};

const ORDEM_DOS_RESULTADOS: readonly ResultadoDoDestinatario[] = [
  "respondeu", "lida", "entregue", "enviada", "pendente", "adiada",
  "limite_do_destinatario", "parou_de_receber", "sem_whatsapp", "falhou", "pulada",
];

const ROTULO: Readonly<Record<ResultadoDoDestinatario, string>> = {
  pendente: "Pendentes", adiada: "Adiadas", enviada: "Enviadas", entregue: "Entregues", lida: "Lidas", respondeu: "Responderam",
  sem_whatsapp: "Sem WhatsApp", parou_de_receber: "Pararam de receber", limite_do_destinatario: "Seguradas pela Meta",
  falhou: "Falharam", pulada: "Puladas",
};

const TITULO = "Campanhas";
const FRASE = "Um template aprovado para quem pediu para receber. O custo da Meta e as conversas da franquia aparecem antes de você agendar.";

const VISTAS: VistaDaLista<Campanha>[] = [
  { id: "todas", rotulo: "Todas as campanhas", passa: () => true },
  { id: "andamento", rotulo: "Em andamento", passa: (k) => k.estado === "agendada" || k.estado === "enviando" || k.estado === "pausada" },
  { id: "concluidas", rotulo: "Concluídas", passa: (k) => k.estado === "concluida" },
  { id: "canceladas", rotulo: "Canceladas", passa: (k) => k.estado === "cancelada" },
  { id: "rascunhos", rotulo: "Rascunhos", passa: (k) => k.estado === "rascunho" },
];

/** A frase do servidor quando ele deu uma; sem ela, o chamador usa a sua. */
function mensagemDa(f: FalhaDaApi | undefined): string | undefined {
  if (!f) return undefined;
  return "erro" in f && f.erro && typeof f.erro === "object" && "mensagem" in f.erro ? String((f.erro as { mensagem?: unknown }).mensagem ?? "") || undefined : undefined;
}

const reais = (centavos: number) => `R$ ${(centavos / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const data = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString("pt-BR") : "—");
const tipoDe = (k: Campanha) => (k.categoria ? k.categoria.charAt(0) + k.categoria.slice(1).toLowerCase() : "Template");

export function Campanhas() {
  const sessao = useSessao();
  const q = useCampanhas();
  const [, navegar] = useLocation();
  // A campanha aberta é uma URL própria (`/campanhas/<id>`): recarregar não perde o lugar, e "olha
  // essa campanha" vira um link. O `?campanha=<id>` de antes redireciona para lá.
  const abrir = (id: string) => navegar(`/campanhas/${encodeURIComponent(id)}`);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const id = new URLSearchParams(window.location.search).get("campanha");
    if (id) navegar(`/campanhas/${encodeURIComponent(id)}`, { replace: true });
  }, [navegar]);
  const [criando, setCriando] = useState(false);
  const podeDisparar = sessao.permissoes.includes("send_campaign");

  if (criando) return <NovaCampanha aoFechar={(id) => { setCriando(false); if (id) abrir(id); }} />;

  if (q.isError) {
    return (
      <PaginaInteira rotulo={TITULO}>
        <CabecalhoDaTela icone={<Megaphone size={18} />} titulo={TITULO} dica={FRASE} />
        <div className="v2-cartao"><Falha erro={q.error} aoTentarDeNovo={() => void q.refetch()} /></div>
      </PaginaInteira>
    );
  }

  const colunas: ColunaDaLista<Campanha>[] = [
    { id: "nome", rotulo: "Nome", principal: true, link: true, celula: (k) => k.nome, dica: (k) => k.nome, ordenar: (k) => k.nome },
    { id: "tipo", rotulo: "Tipo", celula: tipoDe, ordenar: tipoDe },
    { id: "estado", rotulo: "Estado", celula: (k) => <Selo tom={ESTADO[k.estado].tom}>{ESTADO[k.estado].rotulo}</Selo>, ordenar: (k) => ESTADO[k.estado].rotulo },
    { id: "destinatarios", rotulo: "Destinatários", numerica: true, celula: (k) => k.destinatarios.toLocaleString("pt-BR"), ordenar: (k) => k.destinatarios },
    // Responderam e Custo (spec 23, jornada 5): o resultado da campanha na própria lista, sem abrir uma por uma.
    { id: "responderam", rotulo: "Responderam", numerica: true, celula: (k) => (k.contagens.respondeu ?? 0).toLocaleString("pt-BR"), ordenar: (k) => k.contagens.respondeu ?? 0 },
    {
      id: "custo", rotulo: "Custo", numerica: true,
      // Antes de sair, o real é zero e não diz nada: mostra a estimativa, marcada com "≈".
      celula: (k) => (k.custoRealCentavos > 0 || k.estado === "concluida" || k.estado === "cancelada" ? reais(k.custoRealCentavos) : `≈ ${reais(k.custoEstimadoCentavos)}`),
      dica: (k) => `Real: ${reais(k.custoRealCentavos)} · estimado: ${reais(k.custoEstimadoCentavos)}`,
      ordenar: (k) => k.custoRealCentavos || k.custoEstimadoCentavos,
    },
    { id: "criada", rotulo: "Criada", celula: (k) => data(k.criadaEm), ordenar: (k) => k.criadaEm },
    { id: "fim", rotulo: "Fim", celula: (k) => data(k.concluidaEm ?? k.canceladaEm), ordenar: (k) => k.concluidaEm ?? k.canceladaEm },
  ];

  return (
    <PaginaInteira rotulo={TITULO}>
      <ListView<Campanha>
        titulo={TITULO}
        icone={<Megaphone size={18} />}
        colunas={colunas}
        linhas={q.data?.itens}
        chave={(k) => k.id}
        vistas={VISTAS}
        memoria="hugi:campanhas"
        abrir={{ href: (k) => `/campanhas/${encodeURIComponent(k.id)}`, aoAbrir: (k) => abrir(k.id) }}
        busca={{
          campos: (k) => [k.nome, tipoDe(k), k.templateNome, k.numeroExibicao, k.numeroExibicao ? identificadorLegivel(k.numeroExibicao) : null, k.pausaMotivo],
          placeholder: "Nome, template ou número",
        }}
        contagem={(n) => (n === 1 ? "1 campanha" : `${n} campanhas`)}
        vazio={{
          semNada: <><strong>Nenhuma campanha ainda</strong>{" "}<span>Sincronize os templates em Canais, registre o opt-in dos contatos, e crie a primeira. A prévia diz quantos alcança e quanto custa.</span></>,
          semResultado: "Nenhuma campanha nesta vista.",
        }}
        acoes={
          <>
            <Dica texto={FRASE} />
            <Acao
              tom="primaria"
              desabilitado={!podeDisparar}
              {...(podeDisparar ? {} : { motivo: "Criar campanha exige a permissão send_campaign." })}
              onClick={() => setCriando(true)}
            >
              Nova campanha
            </Acao>
          </>
        }
      />
    </PaginaInteira>
  );
}

/** A página de UMA campanha (`/campanhas/<id>`). A lista fica em `/campanhas`. */
export function CampanhaDetalhe(props: { readonly id: string }) {
  const sessao = useSessao();
  const podeDisparar = sessao.permissoes.includes("send_campaign");
  const [, navegar] = useLocation();
  const q = useCampanhas();
  const voltar = () => navegar("/campanhas");
  const casca = (titulo: string, corpo: ReactNode, dica?: string) => (
    <PaginaInteira rotulo={titulo}>
      <CabecalhoDaTela icone={<Megaphone size={18} />} titulo={titulo} {...(dica ? { dica } : {})} secundarias={<Acao onClick={voltar}>Todas as campanhas</Acao>} />
      <div className="v2-cartao">{corpo}</div>
    </PaginaInteira>
  );
  if (q.isPending) return casca("Campanha", <Carregando>Carregando a campanha…</Carregando>);
  if (q.isError) return casca("Campanha", <Falha erro={q.error} aoTentarDeNovo={() => void q.refetch()} />);
  const k = q.data.itens.find((x) => x.id === props.id);
  if (!k) return casca("Campanha não encontrada", <Vazio>Ela pode ter sido apagada, ou o link está errado.</Vazio>);
  return <CorpoDaCampanha campanha={k} podeDisparar={podeDisparar} aoVoltar={voltar} />;
}

const ID_DO_TITULO = "hugi-campanha-titulo";

function CorpoDaCampanha(props: { readonly campanha: Campanha; readonly podeDisparar: boolean; readonly aoVoltar: () => void }) {
  const { campanha: k, podeDisparar } = props;
  const cliente = useQueryClient();
  const [, navegar] = useLocation();
  const [filtro, setFiltro] = useState<ResultadoDoDestinatario | null>(null);
  const [recado, setRecado] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const dest = useDestinatarios(k.id, filtro);

  const agir = async (fn: () => Promise<{ ok: boolean; falha?: FalhaDaApi }>) => {
    setOcupado(true);
    setRecado(null);
    const r = await fn();
    setOcupado(false);
    if (!r.ok) setRecado(mensagemDa(r.falha) ?? "Não deu certo. Tente de novo.");
  };

  const total = Object.values(k.contagens).reduce((a, b) => a + b, 0);
  const est = useEstatisticasDaCampanha(k.id);
  // Resposta de outra forma (dublê velho, servidor antigo) não pode derrubar o detalhe inteiro.
  const s: EstatisticasDaCampanha | null = est.data && Array.isArray(est.data.funil) ? est.data : null;
  const agendadaPara = new Date(k.agendadaPara).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });

  // As ações do cabeçalho: as mesmas de antes, pelas mesmas rotas. Sem `send_campaign`, desabilitadas.
  const semPermissao = !podeDisparar;
  const acoes: AcaoDoRegistro[] = [
    ...(k.estado === "pausada"
      ? [{ id: "retomar", rotulo: "Retomar", tom: "primaria" as const, desabilitada: semPermissao || ocupado, aoClicar: () => void agir(() => retomarCampanha(cliente, k.id)) }]
      : []),
    ...(k.estado === "agendada" || k.estado === "enviando"
      ? [{ id: "pausar", rotulo: "Pausar", desabilitada: semPermissao || ocupado, aoClicar: () => void agir(() => pausarCampanha(cliente, k.id)) }]
      : []),
    ...(k.estado === "rascunho" || k.estado === "agendada" || k.estado === "enviando" || k.estado === "pausada"
      ? [{
          id: "cancelar", rotulo: "Cancelar", tom: "perigo" as const, desabilitada: semPermissao || ocupado,
          aoClicar: () => { if (window.confirm("Cancelar o que ainda não saiu? O que já foi entregue fica.")) void agir(() => cancelarCampanha(cliente, k.id)); },
        }]
      : []),
  ];

  /*
    AS PONTES (spec 23, jornada 5): o número é uma porta. "Responderam" filtra a tabela por quem respondeu (e
    "Destinatários" tira o filtro); "Leads" abre o funil já recortado nesta campanha (`/funil?campanha=<id>`). Enviadas
    e Entregues NÃO filtram: o degrau do funil é cumulativo (quem leu também foi entregue) e o filtro da tabela é por
    resultado exato — a contagem da tabela não bateria com o número clicado.
  */
  const filtrando = (r: ResultadoDoDestinatario | null) => (r !== null && filtro === r ? "filtrando a tabela" : undefined);
  const ICONE_DO_DEGRAU: Readonly<Record<string, { icone: ReactNode; tom: Metrica["tom"]; filtra?: ResultadoDoDestinatario | null }>> = {
    "Destinatários": { icone: <Users size={16} />, tom: "eq", filtra: null },
    Enviadas: { icone: <Send size={16} />, tom: "tg" },
    Entregues: { icone: <CheckCheck size={16} />, tom: "wa" },
    Responderam: { icone: <MessageCircleReply size={16} />, tom: "hugi", filtra: "respondeu" },
  };
  const metricas: Metrica[] = [
    ...(s
      // Um cartão por degrau conhecido (o primeiro de cada nome): degrau novo do servidor não vira cartão sem ícone.
      ? s.funil.filter((d, i, todos) => d.nome in ICONE_DO_DEGRAU && todos.findIndex((x) => x.nome === d.nome) === i).map((d, i) => {
          const jeito = ICONE_DO_DEGRAU[d.nome]!;
          const filtra = jeito.filtra;
          const detalhe = filtra !== undefined && filtrando(filtra) ? filtrando(filtra) : i === 0 ? undefined : `${pct(d.taxaDoAnterior)} do degrau anterior`;
          return {
            id: `funil-${d.nome}`, icone: jeito.icone, tom: jeito.tom, rotulo: d.nome, valor: d.quantidade.toLocaleString("pt-BR"),
            ...(detalhe ? { detalhe } : {}),
            ...(filtra !== undefined ? { aoAbrir: () => setFiltro(filtra), dica: filtra ? `Mostrar só quem ${d.nome.toLowerCase()}` : "Mostrar todos os destinatários" } : {}),
          };
        })
      : [{ id: "destinatarios", icone: <Users size={16} />, tom: "eq" as const, rotulo: "Destinatários", valor: k.destinatarios.toLocaleString("pt-BR"), aoAbrir: () => setFiltro(null) }]),
    // Custo por resposta vai na linha de baixo do custo: oito cartões não cabem numa fileira a 1366.
    {
      id: "custo", icone: <Wallet size={16} />, tom: "fx", rotulo: "Custo real", valor: reais(s?.custoRealCentavos ?? k.custoRealCentavos),
      detalhe: s ? (s.custoPorRespostaCentavos !== null ? `${reais(s.custoPorRespostaCentavos)} por resposta` : "ninguém respondeu") : "mensagens entregues",
      dica: "Pelas mensagens entregues, na tarifa da Meta.",
    },
    { id: "optouts", icone: <UserMinus size={16} />, tom: "neutro", rotulo: "Opt-outs", valor: s ? s.optOuts.toLocaleString("pt-BR") : null, ...(s ? { detalhe: `${pct(s.taxaDeOptOut)} dos destinatários` } : {}) },
    {
      id: "leads", icone: <SquareKanban size={16} />, tom: "ag", rotulo: "Leads ganhos", valor: s ? s.leads.ganhos.toLocaleString("pt-BR") : null,
      ...(s ? { detalhe: `${s.leads.abertos} abertos · ${s.leads.perdidos} perdidos` } : {}),
      dica: "Abrir o funil só com os leads desta campanha",
      aoAbrir: () => navegar(`/funil?campanha=${encodeURIComponent(k.id)}`),
    },
  ];

  const pausa = k.estado === "pausada" ? (
    <p className="hugi-alerta" role="alert">
      Pausada{k.pausadaEm ? ` em ${new Date(k.pausadaEm).toLocaleString("pt-BR")}` : ""}: {k.pausaMotivo}
      {k.pausadaPor?.startsWith("meta:") ? `${/[.!?]$/.test(k.pausaMotivo ?? "") ? "" : "."} A campanha parou sozinha; retomar é decisão sua.` : ""}
    </p>
  ) : null;
  const rodape = pausa || recado ? (
    <>
      {pausa}
      {recado ? <p className="hugi-alerta is-erro" role="alert">{recado}</p> : null}
    </>
  ) : null;

  return (
    <PaginaDeRegistro
      idDoTitulo={ID_DO_TITULO}
      trilha={[{ rotulo: TITULO, href: "/campanhas", aoClicar: props.aoVoltar }, { rotulo: k.nome }]}
      rotulos={{ principal: "Destinatários", lateral: "Detalhes" }}
      destaque={
        <Destaque
          icone={<Megaphone size={18} />}
          tipo="Campanha"
          titulo={k.nome}
          idDoTitulo={ID_DO_TITULO}
          campos={[
            { rotulo: "Estado", valor: <Selo tom={ESTADO[k.estado].tom}>{ESTADO[k.estado].rotulo}</Selo> },
            { rotulo: "Agendada para", valor: agendadaPara },
            { rotulo: "Template", valor: <code className="hugi-mono">{k.templateNome}</code>, dica: k.templateNome },
            { rotulo: "Número", valor: k.numeroExibicao ? identificadorLegivel(k.numeroExibicao) : "—", ausente: !k.numeroExibicao },
            { rotulo: "Destinatários", valor: k.destinatarios.toLocaleString("pt-BR") },
          ]}
          acoes={
            <BarraDeAcoes acoes={acoes} visiveis={4} />
          }
          {...(rodape ? { rodape } : {})}
        />
      }
      caminho={<div className="hugi-campanha-metricas"><Metricas rotulo="Estatísticas da campanha" itens={metricas} /></div>}
      principal={
        <section className="v2-cartao hugi-cartao-elastico" aria-label="Destinatários">
          <header className="hugi-cartao-cabecalho">
            <h2>Destinatários</h2>
            {semPermissao ? <Dica texto="Pausar, retomar e cancelar exigem send_campaign." /> : null}
            <ul className="hugi-chips">
              {ORDEM_DOS_RESULTADOS.filter((r) => k.contagens[r] > 0 || r === filtro).map((r) => (
                <li key={r}>
                  <button type="button" className="hugi-chip" aria-pressed={filtro === r} title={k.frases[r]} onClick={() => setFiltro(filtro === r ? null : r)}>
                    {ROTULO[r]}: {k.contagens[r]}
                  </button>
                </li>
              ))}
            </ul>
          </header>
          <div className="hugi-corpo">
            {total === 0 ? <Vazio>Sem destinatários.</Vazio> : null}
            {dest.data && dest.data.itens.length > 0 ? (
              <table className="v2-tabela">
                <thead>
                  <tr><th scope="col">Contato</th><th scope="col">Número</th><th scope="col">Resultado</th><th scope="col">Detalhe</th></tr>
                </thead>
                <tbody>
                  {dest.data.itens.map((d) => (
                    <tr key={d.contatoId}>
                      {/* Quem respondeu tem conversa: o nome abre o atendimento dela (`/atender/<id>`, o outro build). */}
                      <td className="is-titulo">
                        {d.atendimentoId ? (
                          <a className="v2-link" href={`/atender/${encodeURIComponent(d.atendimentoId)}`} title="Abrir a conversa">{d.nome ?? "Sem nome"}</a>
                        ) : (d.nome ?? "—")}
                      </td>
                      <td className="hugi-mono">{identificadorLegivel(d.identificador)}</td>
                      <td>{ROTULO[d.resultado]}</td>
                      <td title={d.erro ?? undefined}>{d.erro ?? ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
          </div>
        </section>
      }
      lateral={
        <div className="hugi-pilha">
          <section className="v2-cartao hugi-secao" aria-label="Campanha">
            <h3>Campanha</h3>
            <dl className="hugi-pares">
              <Par rotulo="Ritmo" valor={`${k.ritmoPorMinuto}/min`} />
              {k.categoria ? <Par rotulo="Categoria" valor={k.categoria} /> : null}
              <Par rotulo="Tarifa por mensagem" valor={reais(k.tarifaCentavosPorMensagem)} />
              <Par rotulo="Custo estimado" valor={reais(k.custoEstimadoCentavos)} dica="Pela tarifa da Meta, antes do envio." />
            </dl>
          </section>
          {s ? (
            <>
              <section className="v2-cartao hugi-secao" aria-label="Tempo mediano">
                <h3>Tempo mediano</h3>
                <dl className="hugi-pares">
                  <Par rotulo="Até entregar" valor={tempo(s.tempos.ateEntrega)} />
                  <Par rotulo="Até ler" valor={tempo(s.tempos.ateLeitura)} />
                  <Par rotulo="Até responder" valor={tempo(s.tempos.ateResposta)} />
                </dl>
              </section>
              <section className="v2-cartao hugi-secao" aria-label="Fora do funil">
                <h3>Fora do funil</h3>
                <dl className="hugi-pares">
                  <Par rotulo="Lidas com recibo" valor={s.lidas} dica="Depende de o destinatário ter a confirmação de leitura ligada." />
                  <Par rotulo="Seguradas pela Meta" valor={s.seguradasPelaMeta} />
                  <Par rotulo="Custo por lead ganho" valor={s.custoPorLeadGanhoCentavos === null ? "—" : reais(s.custoPorLeadGanhoCentavos)} />
                  <Par rotulo="Conversas abertas" valor={`${s.conversas.abertas} · ${s.conversas.finalizadas} finalizadas`} />
                </dl>
              </section>
              {s.saude.qualidadeNoInicio || s.saude.limiteNoInicio ? (
                <section className="v2-cartao hugi-secao" aria-label="Saúde do template">
                  <h3>Saúde do template</h3>
                  <dl className="hugi-pares">
                    <Par rotulo="Qualidade" valor={`${s.saude.qualidadeNoInicio ?? "?"} → ${s.saude.qualidadeNoFim ?? "em andamento"}`} />
                    <Par rotulo="Limite do número" valor={`${s.saude.limiteNoInicio ?? "?"} → ${s.saude.limiteNoFim ?? "em andamento"}`} />
                  </dl>
                </section>
              ) : null}
            </>
          ) : null}
        </div>
      }
    />
  );
}

function Par(props: { readonly rotulo: string; readonly valor: ReactNode; readonly dica?: string }) {
  return <div title={props.dica}><dt>{props.rotulo}</dt><dd>{props.valor}</dd></div>;
}

const pct = (x: number | null) => (x === null ? "—" : `${Math.round(x * 100)}%`);
const tempo = (s: number | null) => {
  if (s === null) return "—";
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)} min`;
  if (s < 86400) return `${(s / 3600).toFixed(1)} h`;
  return `${(s / 86400).toFixed(1)} d`;
};

/**
 * Enter num campo de uma linha NÃO envia o cadastro: o envio aqui agenda mensagens pagas, e antes da
 * v2 não havia formulário — Enter não fazia nada. O `<form>` é do kit; a tecla fica presa aqui.
 */
const semEnterQueEnvia = (e: KeyboardEvent<HTMLDivElement>) => {
  if (e.key === "Enter" && (e.target as HTMLElement).tagName === "INPUT") e.preventDefault();
};

/** A espera entre a última mudança e o pedido da prévia: digitar "Vitória" não faz sete pedidos. */
export const ESPERA_DA_PREVIA_MS = 500;

/*
  NOVA CAMPANHA (spec 23, jornada 5 — aprovada pelo owner em 01/11): três blocos numerados, Mensagem → Público →
  Quando, e o CUSTO SEMPRE AO LADO DO BOTÃO.

  ⚠️ POR QUE O CUSTO SE RECALCULA SOZINHO: até 01/11 havia um botão "Conferir quantos alcança e quanto custa", e o
  rótulo da primária ("Agendar para 280 — R$ 98") ficava VELHO quando a pessoa mexia em etiqueta, dias, atributo,
  quando, ritmo ou variável depois de conferir — o botão prometia um custo que já não valia. Agora toda mudança de
  campo que vai no corpo da prévia apaga o número, espera ~500 ms e pede de novo À MESMA ROTA
  (`POST /campanhas/previa`, a mesma função que grava); a primária fica desabilitada enquanto calcula. Pedido que
  chega atrasado (de um corpo que já mudou) é descartado.

  ⚠️ D7 (owner, 01/11): "Quando" não tem mais padrão de "agora + 10 min". A pessoa escolhe "Agora" (o corpo vai sem
  `agendadaPara` — o servidor usa o instante do pedido) ou "Escolher data". Sem escolha, não agenda.

  Ritmo e atributo da planilha ficam em "Avançado", fechado; o limite diário da Meta virou um (i) no bloco Quando.
*/
function NovaCampanha(props: { readonly aoFechar: (id: string | null) => void }) {
  const cliente = useQueryClient();
  const conexoes = useConexoes();
  const catalogos = useCatalogos();
  const templates = useTemplates(true);

  const [nome, setNome] = useState("");
  const [conexaoId, setConexaoId] = useState("");
  const [templateChave, setTemplateChave] = useState("");
  const [variaveis, setVariaveis] = useState<string[]>([]);
  // Um valor por botão do template aprovado, POR ÍNDICE — a Meta casa valor e botão por ele, e um
  // índice trocado preenche o botão errado sem erro nenhum.
  const [valoresDeBotao, setValoresDeBotao] = useState<string[]>([]);
  const [etiquetas, setEtiquetas] = useState<string[]>([]);
  const [modo, setModo] = useState<"qualquer" | "todas">("qualquer");
  const [dias, setDias] = useState("");
  const [atributo, setAtributo] = useState({ chave: "", valor: "" });
  // D7: sem padrão. `null` = a pessoa ainda não disse quando.
  const [quando, setQuando] = useState<"agora" | "data" | null>(null);
  const [data, setData] = useState("");
  const [ritmo, setRitmo] = useState("60");
  const [previa, setPrevia] = useState<PreviaDaCampanha | null>(null);
  const [calculando, setCalculando] = useState(false);
  const [erroDaPrevia, setErroDaPrevia] = useState<string | null>(null);
  // Sobe a cada "Ler o limite na Meta": o limite mudou no servidor, o corpo não — a prévia precisa ser pedida de novo.
  const [rodada, setRodada] = useState(0);
  const [erro, setErro] = useState<string | null>(null);
  const [agendando, setAgendando] = useState(false);

  const oficiais = (conexoes.data?.conexoes ?? []).filter((c) => c.canal === "whatsapp" && c.modo !== "nao_oficial" && c.estado !== "revogada");
  const daConexao = (templates.data?.templates ?? []).filter((t) => t.conexaoId === conexaoId && t.estado === "aprovado");
  const template = daConexao.find((t) => `${t.nome}:${t.idioma}` === templateChave) ?? null;

  useEffect(() => {
    if (!template) return;
    setVariaveis((v) => Array.from({ length: template.variaveis }, (_, i) => v[i] ?? (i === 0 ? "{{contato.nome}}" : "")));
    setValoresDeBotao((v) => Array.from({ length: template.botoes?.length ?? 0 }, (_, i) => v[i] ?? ""));
  }, [template]);

  const botoesQueExigem = (template?.botoes ?? []).filter((b) => b.exigeValor);

  const corpo = () => ({
    nome,
    conexaoId,
    templateNome: template?.nome,
    templateIdioma: template?.idioma,
    templateVariaveis: variaveis,
    // Só vai quando há algo a preencher: componente de botão vazio faz a Meta recusar a mensagem.
    ...(botoesQueExigem.length ? { templateBotoes: valoresDeBotao } : {}),
    segmento: {
      etiquetas,
      etiquetasModo: modo,
      ...(dias ? { ultimoContatoDias: Number(dias) } : {}),
      ...(atributo.chave && atributo.valor ? { metadados: [atributo] } : {}),
    },
    // "Agora" (e o ainda-não-escolhido, na prévia) vai sem horário: o servidor usa o instante do pedido.
    agendadaPara: quando === "data" && data ? new Date(data).toISOString() : null,
    ritmoPorMinuto: Number(ritmo) || 60,
  });

  // O que a prévia lê: tudo do corpo menos o nome (que não muda público nem custo).
  const chaveDaPrevia = template ? JSON.stringify({ ...corpo(), nome: undefined }) : "";

  useEffect(() => {
    setPrevia(null);
    setErroDaPrevia(null);
    if (!chaveDaPrevia) { setCalculando(false); return; }
    setCalculando(true);
    let viva = true;
    const espera = setTimeout(() => {
      void previaDeCampanha(JSON.parse(chaveDaPrevia) as Record<string, unknown>).then((r) => {
        if (!viva) return; // o corpo mudou enquanto o pedido voava: esta resposta já não vale
        setCalculando(false);
        if (r.ok) setPrevia(r.dados);
        else setErroDaPrevia(mensagemDa(r.falha) ?? "Não foi possível calcular o público e o custo.");
      });
    }, ESPERA_DA_PREVIA_MS);
    return () => { viva = false; clearTimeout(espera); };
  }, [chaveDaPrevia, rodada]);

  const agendar = async () => {
    setAgendando(true);
    setErro(null);
    const r = await criarCampanha(cliente, corpo());
    setAgendando(false);
    if (r.ok) props.aoFechar(r.dados.id);
    else setErro(mensagemDa(r.falha) ?? "Não foi possível agendar.");
  };

  const lerLimite = async () => {
    if (!conexaoId) return;
    const r = await sincronizarLimiteDoNumero(conexaoId);
    if (!r.ok) { setErro(mensagemDa(r.falha) ?? "A Meta não respondeu o limite do número."); return; }
    setRodada((n) => n + 1);
  };

  // Uma primária: "Agendar" / "Enviar agora", que só vale com a prévia DESTE corpo. Antes, o motivo diz o que falta.
  const motivo = !template
    ? "Escolha o número e o template."
    : calculando
      ? "Calculando quantos alcança e quanto custa…"
      : erroDaPrevia
        ? erroDaPrevia
        : !previa
          ? "Calculando quantos alcança e quanto custa…"
          : previa.destinatarios === 0
            ? "Nenhum destinatário alcançável."
            : quando === null
              ? "Escolha quando: agora ou numa data."
              : quando === "data" && !data
                ? "Escolha a data e a hora."
                : null;

  const verbo = quando === "agora" ? "Enviar agora" : "Agendar";
  const rotuloDaPrimaria = previa && !calculando
    ? `${verbo} para ${contar(previa.destinatarios, "contato", "contatos")} — ${reais(previa.custoEstimadoCentavos)}`
    : verbo;

  const limite = previa?.limiteDiario;
  const dicaDoLimite = limite
    ? `A Meta limita quantas pessoas novas o número aborda por dia: ${limite.limite.toLocaleString("pt-BR")}${limite.suposto ? " (suposto — leia o real na Meta, em Avançado)" : ""}. Acima disso a campanha continua no dia seguinte.`
    : "A Meta limita quantas pessoas novas o número aborda por dia. Acima disso a campanha continua no dia seguinte.";

  // O custo AO LADO do botão (rodapé do cadastro): o número, ou "calculando", nunca um número velho.
  const custo = (
    <span className="hugi-custo-ao-vivo" role="status" aria-live="polite" data-calculando={calculando ? "1" : undefined}>
      {!template ? null : calculando || (!previa && !erroDaPrevia) ? (
        <span className="is-fraco">Calculando…</span>
      ) : previa ? (
        <>
          <strong>{reais(previa.custoEstimadoCentavos)}</strong>
          <span>{contar(previa.conversasDaFranquia, "conversa da franquia", "conversas da franquia")}</span>
          {previa.dias > 1 ? <span>em {previa.dias} dias</span> : null}
        </>
      ) : null}
    </span>
  );

  return (
    <PaginaDeCadastro
      icone={<Megaphone size={18} />}
      titulo="Nova campanha"
      dica={FRASE}
      trilha={[{ rotulo: TITULO, href: "/campanhas", aoClicar: () => props.aoFechar(null) }]}
      rotuloDaPrimaria={rotuloDaPrimaria}
      aoSalvar={() => void agendar()}
      aoCancelar={() => props.aoFechar(null)}
      rotuloDoCancelar="Voltar"
      salvando={agendando}
      motivo={agendando ? null : motivo}
      erro={erro}
      extraDoRodape={custo}
    >
      <div className="v2-cadastro-campos hugi-cadastro" onKeyDown={semEnterQueEnvia} style={{ gridColumn: "1 / -1" }}>
        <BlocoDoCadastro titulo="1 · Mensagem" largo>
          <CampoDoCadastro rotulo="Nome">
            {(p) => <input {...p} value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Agenda de sábado" />}
          </CampoDoCadastro>
          <CampoDoCadastro rotulo="Por qual número">
            {(p) => (
              <select {...p} value={conexaoId} onChange={(e) => { setConexaoId(e.target.value); setTemplateChave(""); }}>
                <option value="">Escolha o número (API oficial)</option>
                {oficiais.map((c) => <option key={c.id} value={c.id}>{c.apelido ?? (c.numeroExibicao ? identificadorLegivel(c.numeroExibicao) : c.id)}</option>)}
              </select>
            )}
          </CampoDoCadastro>
          <CampoDoCadastro rotulo="Template aprovado">
            {(p) => (
              <select {...p} value={templateChave} disabled={!conexaoId} onChange={(e) => setTemplateChave(e.target.value)}>
                <option value="">{conexaoId ? (daConexao.length ? "Escolha o template" : "Nenhum aprovado — sincronize em Canais") : "Escolha o número antes"}</option>
                {daConexao.map((t) => (
                  <option key={`${t.nome}:${t.idioma}`} value={`${t.nome}:${t.idioma}`} disabled={t.bloqueio !== null}>
                    {t.nome} ({t.idioma}) · {t.categoria ?? "?"}{t.qualidade ? ` · ${t.qualidade}` : ""}{t.bloqueio ? ` · BLOQUEADO (${t.bloqueio.motivo})` : ""}
                  </option>
                ))}
              </select>
            )}
          </CampoDoCadastro>
          {template ? (
            <div className="v2-cadastro-campo">
              <span className="hugi-grupos-nota">Corpo</span>
              <p className="hugi-corpo-do-template">{template.corpo}</p>
              {template.sempreCobrado ? <span><Selo tom="atencao">marketing: sempre cobrado</Selo></span> : null}
            </div>
          ) : null}
          {variaveis.map((v, i) => (
            <CampoDoCadastro key={i} rotulo={`Variável {{${i + 1}}}`}>
              {(p) => <input {...p} value={v} onChange={(e) => setVariaveis(variaveis.map((x, j) => (j === i ? e.target.value : x)))} placeholder="texto fixo, {{contato.nome}} ou {{contato.cidade}}" />}
            </CampoDoCadastro>
          ))}
          {botoesQueExigem.map((b) => (
            <CampoDoCadastro key={b.indice} rotulo={`Botão ${b.indice + 1}${b.texto ? ` — "${b.texto}"` : ""} (${b.tipo === "COPY_CODE" ? "código do cupom" : "final da URL"})`}>
              {(p) => (
                <input
                  {...p}
                  value={valoresDeBotao[b.indice] ?? ""}
                  onChange={(e) => setValoresDeBotao(valoresDeBotao.map((x, j) => (j === b.indice ? e.target.value : x)))}
                  placeholder={b.tipo === "COPY_CODE" ? "PROMO25" : "planos ou {{contato.cidade}}"}
                />
              )}
            </CampoDoCadastro>
          ))}
          {template?.carrossel ? (
            /*
              O carrossel exige um cartão por cartão aprovado, com imagem em cada um — e montá-los aqui
              é uma frente própria. Dizer isto NA TELA é o que impede a pessoa de agendar e receber a
              recusa da guarda sem entender de onde ela vem.
            */
            <p className="hugi-alerta" style={{ gridColumn: "1 / -1" }}>
              Este template é um <strong>carrossel</strong>. A campanha ainda não monta os cartões (cada um
              precisa da própria imagem), então ela vai ser recusada ao agendar. Use um template sem cartões.
            </p>
          ) : null}
        </BlocoDoCadastro>

        <BlocoDoCadastro titulo="2 · Público" dica="Só quem tem opt-in registrado e não está bloqueado." largo>
          <div className="hugi-opcoes" style={{ gridColumn: "1 / -1" }}>
            {(catalogos.data?.etiquetas ?? []).map((e) => (
              <label key={e.id}>
                <input type="checkbox" checked={etiquetas.includes(e.id)} onChange={(ev) => setEtiquetas(ev.target.checked ? [...etiquetas, e.id] : etiquetas.filter((x) => x !== e.id))} />
                {e.nome}
              </label>
            ))}
            {etiquetas.length > 1 ? (
              <select className="campo" aria-label="Como combinar as etiquetas" value={modo} onChange={(e) => setModo(e.target.value as "qualquer" | "todas")}>
                <option value="qualquer">qualquer uma das etiquetas</option>
                <option value="todas">todas as etiquetas</option>
              </select>
            ) : null}
          </div>
          <CampoDoCadastro rotulo="Escreveu nos últimos (dias)">
            {(p) => <input {...p} inputMode="numeric" value={dias} onChange={(e) => setDias(e.target.value.replace(/\D/g, ""))} placeholder="qualquer" />}
          </CampoDoCadastro>
          {previa && !calculando ? (
            <div className="hugi-alcance" aria-label="Quem a campanha alcança">
              <span><strong>{previa.destinatarios.toLocaleString("pt-BR")}</strong> recebem</span>
              <span>{previa.previa.casam} casam com o filtro</span>
              <span>{previa.previa.semOptin} sem opt-in</span>
              <span>{previa.previa.bloqueados} bloqueados</span>
              <span>{previa.previa.semWhatsapp} sem WhatsApp</span>
            </div>
          ) : null}
          {previa && !calculando ? previa.avisos.map((a) => <p key={a} className="hugi-alerta" style={{ gridColumn: "1 / -1" }}>{a}</p>) : null}
        </BlocoDoCadastro>

        <BlocoDoCadastro titulo="3 · Quando" dica={dicaDoLimite} largo>
          <div className="hugi-opcoes" role="radiogroup" aria-label="Quando a campanha começa" style={{ gridColumn: "1 / -1" }}>
            <label><input type="radio" name="quando-campanha" checked={quando === "agora"} onChange={() => setQuando("agora")} />Agora</label>
            <label><input type="radio" name="quando-campanha" checked={quando === "data"} onChange={() => setQuando("data")} />Escolher data</label>
          </div>
          {quando === "data" ? (
            <CampoDoCadastro rotulo="Data e hora">
              {(p) => <input {...p} type="datetime-local" value={data} onChange={(e) => setData(e.target.value)} />}
            </CampoDoCadastro>
          ) : null}
        </BlocoDoCadastro>

        <details className="hugi-avancado">
          <summary>Avançado</summary>
          <div className="v2-cadastro-campos">
            <CampoDoCadastro rotulo="Ritmo (mensagens por minuto)">
              {(p) => <input {...p} inputMode="numeric" value={ritmo} onChange={(e) => setRitmo(e.target.value.replace(/\D/g, ""))} />}
            </CampoDoCadastro>
            <span />
            <CampoDoCadastro rotulo="Atributo (da planilha)">
              {(p) => <input {...p} value={atributo.chave} onChange={(e) => setAtributo({ ...atributo, chave: e.target.value })} placeholder="cidade" />}
            </CampoDoCadastro>
            <CampoDoCadastro rotulo="igual a">
              {(p) => <input {...p} value={atributo.valor} onChange={(e) => setAtributo({ ...atributo, valor: e.target.value })} placeholder="Vitória" />}
            </CampoDoCadastro>
            <div className="hugi-botoes-do-corpo">
              <Acao pequeno desabilitado={!conexaoId} {...(conexaoId ? {} : { motivo: "Escolha o número." })} onClick={() => void lerLimite()}>
                Ler o limite diário do número na Meta
              </Acao>
            </div>
          </div>
        </details>
      </div>
    </PaginaDeCadastro>
  );
}

// --- Miúdos da tela ---------------------------------------------------------------------------------

/** O estado de falha da área, com os estados do kit (e o da casca quando é caso comercial ou de versão). */
function Falha(props: { readonly erro: unknown; readonly aoTentarDeNovo?: () => void }) {
  const f = falhaDe(props.erro);
  if (f?.tipo === "sem_permissao") return <SemPermissao>{f.permissao ? textos.estados.semPermissao.qual(f.permissao) : textos.estados.semPermissao.semNome}</SemPermissao>;
  if (f?.tipo === "sem_entitlement" || f?.tipo === "versao_incompativel") return <EstadoDaFalha falha={f} />;
  return <Erro {...(props.aoTentarDeNovo ? { aoTentarDeNovo: props.aoTentarDeNovo } : {})}>{f?.erro.mensagem ?? textos.estados.erro.corpo}</Erro>;
}

/** Botão do kit; desabilitado, diz o motivo (no `title` e ligado por `aria-describedby`). */
function Acao(props: {
  readonly children: ReactNode;
  readonly onClick?: () => void;
  readonly tom?: "primaria" | "perigo";
  readonly pequeno?: boolean;
  readonly desabilitado?: boolean;
  readonly motivo?: string;
}) {
  const id = useId();
  const comMotivo = Boolean(props.desabilitado && props.motivo);
  return (
    <>
      <button
        type="button"
        className={`v2-botao${props.tom === "primaria" ? " is-primario" : props.tom === "perigo" ? " is-perigo" : ""}${props.pequeno ? " is-pequeno" : ""}`}
        disabled={props.desabilitado}
        title={comMotivo ? props.motivo : undefined}
        aria-describedby={comMotivo ? id : undefined}
        onClick={props.onClick}
      >
        {props.children}
      </button>
      {comMotivo ? <span id={id} className="v2-sr">{props.motivo}</span> : null}
    </>
  );
}
